import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";

import {IFavoritesEditExternalOfferDetailFormValues} from "../../favorites/constants/favorites_edit_external_offer_detail_form";
import {IFavoritesEditExternalOfferFormValues, IFavoritesEditExternalOfferRoomsValue} from "../../favorites/constants/favorites_edit_external_offer_form";
import {IFavoriteListItemDTO} from "../../favorites/list/types/IFavoriteListItemDTO";
import {parseExternalFavoriteDecimal} from "../../favorites/list/utils/get_external_favorite_list_card_display";
import {getPlacesAddressLabel} from "../../favorites/places/utils/create_places_address_form_value";
import {isExternalNeighborhoodDetectiveAvailable} from "../../neighborhood_detective/utils/is_neighborhood_detective_available";

const getNullableString = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim();

    return trimmed ? trimmed : null;
};

const parseOptionalDecimal = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    return parseExternalFavoriteDecimal(trimmed);
};

const parseOptionalInteger = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    const parsed = Number.parseInt(trimmed, 10);

    return Number.isFinite(parsed) ? parsed : null;
};

export const formatExternalFavoriteVariantRooms = (rooms: number | null | undefined): number | string | null => {
    if (rooms == null) return null;

    return rooms >= 5 ? "5+" : rooms;
};

const formatFormVariantRooms = (rooms: IFavoritesEditExternalOfferRoomsValue): number | string | null => {
    if (!rooms) return null;

    const roomValue = rooms.upper ?? rooms.lower;

    if (roomValue === null || roomValue === undefined || roomValue === "") return null;

    return roomValue === 5 ? "5+" : roomValue;
};

export const normalizeExternalSourceFromPortalLabel = (portalLabel: string | null | undefined): string | null => {
    const trimmed = portalLabel?.trim();

    if (!trimmed) return null;

    return trimmed.toLowerCase().replace(/\s+/g, "_");
};

export const getExternalSourceFromFavorite = (favorite: IFavoriteListItemDTO): string | null =>
    normalizeExternalSourceFromPortalLabel(favorite.external_property?.portal_label);

export const getAmplitudeExternalFavoriteSourceProperties = (favorite: IFavoriteListItemDTO) => ({
    external_source: getExternalSourceFromFavorite(favorite),
    item_id: null,
    offer_source: "external" as const,
    offer_url: getNullableString(favorite.external_property?.source_url),
    property_id: null
});

export const getAmplitudeExternalFavoriteItemProperties = (favorite: IFavoriteListItemDTO) => {
    const price = parseExternalFavoriteDecimal(favorite.price);
    const priceM2 = parseExternalFavoriteDecimal(favorite.price_m2);
    const area = parseExternalFavoriteDecimal(favorite.area);

    return {
        currency: price != null ? "PLN" : null,
        item_address: getNullableString(favorite.location),
        item_brand: null,
        item_name: getNullableString(favorite.name),
        price,
        price_m2: priceM2,
        variant_area: area,
        variant_floor: favorite.floor ?? null,
        variant_name: null,
        variant_rooms: formatExternalFavoriteVariantRooms(favorite.rooms)
    };
};

export enum ExternalFavoriteTrackingLocation {
    DASHBOARD = "km_dashboard",
    FAVOURITE = "km_favourite"
}

export const getAmplitudeExternalFavoriteProperties = (
    favorite: IFavoriteListItemDTO,
    location: ExternalFavoriteTrackingLocation = ExternalFavoriteTrackingLocation.FAVOURITE
) => ({
    location,
    ...getAmplitudeExternalFavoriteSourceProperties(favorite),
    ...getAmplitudeExternalFavoriteItemProperties(favorite)
});

export const getAmplitudeExternalFavoriteListProduct = (favorite: IFavoriteListItemDTO, index: number) => ({
    ask_offer_cta_visible: false,
    km_neighborhood_detective_available: isExternalNeighborhoodDetectiveAvailable(favorite.external_property),
    index,
    item_category: "property",
    ...getAmplitudeExternalFavoriteSourceProperties(favorite),
    ...getAmplitudeExternalFavoriteItemProperties(favorite)
});

export const getAmplitudeRemoveFromExternalFavoriteProperties = (favorite: IFavoriteListItemDTO, index?: number) => ({
    ...getAmplitudeExternalFavoriteProperties(favorite),
    ...(index != null ? {index} : {})
});

export const getAmplitudeExternalFavoriteFormProperties = (
    values: IFavoritesEditExternalOfferFormValues,
    favorite: IFavoriteListItemDTO,
    location: ExternalFavoriteTrackingLocation = ExternalFavoriteTrackingLocation.FAVOURITE
) => {
    const hasPrice = values.price != null && values.price > 0 && values.price !== MAX_VALUE_INPUT_LIMITER;

    return {
        ...getAmplitudeExternalFavoriteSourceProperties(favorite),
        currency: hasPrice ? "PLN" : null,
        item_address: getNullableString(getPlacesAddressLabel(values.location)),
        item_name: getNullableString(values.name),
        location,
        price: hasPrice ? values.price : null,
        variant_area: parseOptionalDecimal(values.area),
        variant_floor: parseOptionalInteger(values.floor),
        variant_rooms: formatFormVariantRooms(values.rooms)
    };
};

const sumOptionalIntegers = (rawValues: string[]): number | null => {
    let hasValue = false;
    let sum = 0;

    for (const rawValue of rawValues) {
        const parsed = parseOptionalInteger(rawValue);

        if (parsed == null) continue;

        hasValue = true;
        sum += parsed;
    }

    return hasValue ? sum : null;
};

export const getAmplitudeExternalFavoriteDetailFormProperties = (
    values: IFavoritesEditExternalOfferDetailFormValues,
    favorite: IFavoriteListItemDTO,
    description?: string | null
) => {
    const price = parseOptionalDecimal(values.price);
    const parkingPlaces = (values.parking_places ?? []).filter((place) => place.type.trim());
    const parkingCount = sumOptionalIntegers(parkingPlaces.map((place) => place.quantity));
    const parkingPrice = sumOptionalIntegers(parkingPlaces.map((place) => place.price));

    return {
        ...getAmplitudeExternalFavoriteSourceProperties(favorite),
        currency: price != null ? "PLN" : null,
        item_address: getNullableString(getPlacesAddressLabel(values.location)),
        item_description: getNullableString(description ?? favorite.description),
        item_name: getNullableString(favorite.name),
        location: "km_favourites",
        price,
        variant_area: parseOptionalDecimal(values.area),
        variant_floor: parseOptionalInteger(values.floor),
        variant_parking_count: parkingCount ?? (parkingPlaces.length ? parkingPlaces.length : null),
        variant_parking_price: parkingPrice,
        variant_rooms: formatExternalFavoriteVariantRooms(parseOptionalInteger(values.rooms)),
        variant_storage_count: parseOptionalInteger(values.storage_quantity),
        variant_storage_price: parseOptionalInteger(values.storage_price),
        variant_underground_levels: parseOptionalInteger(values.floors_below_ground)
    };
};
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";

import {IFavoritesEditExternalOfferDetailFormValues} from "../../favorites/constants/favorites_edit_external_offer_detail_form";
import {IFavoritesEditExternalOfferFormValues, IFavoritesEditExternalOfferRoomsValue} from "../../favorites/constants/favorites_edit_external_offer_form";
import {IFavoriteListItemDTO} from "../../favorites/list/types/IFavoriteListItemDTO";
import {parseExternalFavoriteDecimal} from "../../favorites/list/utils/get_external_favorite_list_card_display";
import {getPlacesAddressLabel} from "../../favorites/places/utils/create_places_address_form_value";
import {isExternalNeighborhoodDetectiveAvailable} from "../../neighborhood_detective/utils/is_neighborhood_detective_available";

const getNullableString = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim();

    return trimmed ? trimmed : null;
};

const parseOptionalDecimal = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    return parseExternalFavoriteDecimal(trimmed);
};

const parseOptionalInteger = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    const parsed = Number.parseInt(trimmed, 10);

    return Number.isFinite(parsed) ? parsed : null;
};

export const formatExternalFavoriteVariantRooms = (rooms: number | null | undefined): number | string | null => {
    if (rooms == null) return null;

    return rooms >= 5 ? "5+" : rooms;
};

const formatFormVariantRooms = (rooms: IFavoritesEditExternalOfferRoomsValue): number | string | null => {
    if (!rooms) return null;

    const roomValue = rooms.upper ?? rooms.lower;

    if (roomValue === null || roomValue === undefined || roomValue === "") return null;

    return roomValue === 5 ? "5+" : roomValue;
};

export const normalizeExternalSourceFromPortalLabel = (portalLabel: string | null | undefined): string | null => {
    const trimmed = portalLabel?.trim();

    if (!trimmed) return null;

    return trimmed.toLowerCase().replace(/\s+/g, "_");
};

export const getExternalSourceFromFavorite = (favorite: IFavoriteListItemDTO): string | null =>
    normalizeExternalSourceFromPortalLabel(favorite.external_property?.portal_label);

export const getAmplitudeExternalFavoriteSourceProperties = (favorite: IFavoriteListItemDTO) => ({
    external_source: getExternalSourceFromFavorite(favorite),
    item_id: null,
    offer_source: "external" as const,
    offer_url: getNullableString(favorite.external_property?.source_url),
    property_id: null
});

export const getAmplitudeExternalFavoriteItemProperties = (favorite: IFavoriteListItemDTO) => {
    const price = parseExternalFavoriteDecimal(favorite.price);
    const priceM2 = parseExternalFavoriteDecimal(favorite.price_m2);
    const area = parseExternalFavoriteDecimal(favorite.area);

    return {
        currency: price != null ? "PLN" : null,
        item_address: getNullableString(favorite.location),
        item_brand: null,
        item_name: getNullableString(favorite.name),
        price,
        price_m2: priceM2,
        variant_area: area,
        variant_floor: favorite.floor ?? null,
        variant_name: null,
        variant_rooms: formatExternalFavoriteVariantRooms(favorite.rooms)
    };
};

export enum ExternalFavoriteTrackingLocation {
    DASHBOARD = "km_dashboard",
    FAVOURITE = "km_favourite"
}

export const getAmplitudeExternalFavoriteProperties = (
    favorite: IFavoriteListItemDTO,
    location: ExternalFavoriteTrackingLocation = ExternalFavoriteTrackingLocation.FAVOURITE
) => ({
    location,
    ...getAmplitudeExternalFavoriteSourceProperties(favorite),
    ...getAmplitudeExternalFavoriteItemProperties(favorite)
});

export const getAmplitudeExternalFavoriteListProduct = (favorite: IFavoriteListItemDTO, index: number) => ({
    ask_offer_cta_visible: false,
    km_neighborhood_detective_available: isExternalNeighborhoodDetectiveAvailable(favorite.external_property),
    index,
    item_category: "property",
    ...getAmplitudeExternalFavoriteSourceProperties(favorite),
    ...getAmplitudeExternalFavoriteItemProperties(favorite)
});

export const getAmplitudeRemoveFromExternalFavoriteProperties = (favorite: IFavoriteListItemDTO, index?: number) => ({
    ...getAmplitudeExternalFavoriteProperties(favorite),
    ...(index != null ? {index} : {})
});

export const getAmplitudeExternalFavoriteFormProperties = (
    values: IFavoritesEditExternalOfferFormValues,
    favorite: IFavoriteListItemDTO,
    location: ExternalFavoriteTrackingLocation = ExternalFavoriteTrackingLocation.FAVOURITE
) => {
    const hasPrice = values.price != null && values.price > 0 && values.price !== MAX_VALUE_INPUT_LIMITER;

    return {
        ...getAmplitudeExternalFavoriteSourceProperties(favorite),
        currency: hasPrice ? "PLN" : null,
        item_address: getNullableString(getPlacesAddressLabel(values.location)),
        item_name: getNullableString(values.name),
        location,
        price: hasPrice ? values.price : null,
        variant_area: parseOptionalDecimal(values.area),
        variant_floor: parseOptionalInteger(values.floor),
        variant_rooms: formatFormVariantRooms(values.rooms)
    };
};

const sumOptionalIntegers = (rawValues: string[]): number | null => {
    let hasValue = false;
    let sum = 0;

    for (const rawValue of rawValues) {
        const parsed = parseOptionalInteger(rawValue);

        if (parsed == null) continue;

        hasValue = true;
        sum += parsed;
    }

    return hasValue ? sum : null;
};

export const getAmplitudeExternalFavoriteDetailFormProperties = (
    values: IFavoritesEditExternalOfferDetailFormValues,
    favorite: IFavoriteListItemDTO,
    description?: string | null
) => {
    const price = parseOptionalDecimal(values.price);
    const parkingPlaces = (values.parking_places ?? []).filter((place) => place.type.trim());
    const parkingCount = sumOptionalIntegers(parkingPlaces.map((place) => place.quantity));
    const parkingPrice = sumOptionalIntegers(parkingPlaces.map((place) => place.price));

    return {
        ...getAmplitudeExternalFavoriteSourceProperties(favorite),
        currency: price != null ? "PLN" : null,
        item_address: getNullableString(getPlacesAddressLabel(values.location)),
        item_description: getNullableString(description ?? favorite.description),
        item_name: getNullableString(favorite.name),
        location: "km_favourites",
        price,
        variant_area: parseOptionalDecimal(values.area),
        variant_floor: parseOptionalInteger(values.floor),
        variant_parking_count: parkingCount ?? (parkingPlaces.length ? parkingPlaces.length : null),
        variant_parking_price: parkingPrice,
        variant_rooms: formatExternalFavoriteVariantRooms(parseOptionalInteger(values.rooms)),
        variant_storage_count: parseOptionalInteger(values.storage_quantity),
        variant_storage_price: parseOptionalInteger(values.storage_price),
        variant_underground_levels: parseOptionalInteger(values.floors_below_ground)
    };
};
